import type { InteriorWoodSpecies } from "../domain/finishes/types";
import { resolveBenchSlats } from "../domain/interior/benchSlats";
import { resolveBenchBackrest, type BenchBackrest } from "../domain/interior/benchBackrest";
import { resolveBenchSupport } from "../domain/interior/benchSupport";
import {
	type BenchSkirt,
	resolveBenchSkirt,
} from "../domain/interior/benchSkirt";
import {
	UPPER_BENCH_TOP_BELOW_WALL_TOP_M,
	LOWER_BENCH_TOP_BELOW_WALL_TOP_M,
} from "../domain/interior/constants";
export {
	UPPER_BENCH_TOP_BELOW_WALL_TOP_M,
	LOWER_BENCH_TOP_BELOW_WALL_TOP_M,
} from "../domain/interior/constants";
/**
 * The interior layout, resolved into everything the 3D builders need to draw
 * it — and nothing else.
 *
 * Issue 130 made the lavar and the aggregat user-authored state and derived the
 * BOM from it. The engines kept drawing their own fixed geometry, so the model
 * and the material list were two independent descriptions of the same bench
 * that happened to agree. This module is the seam that removes the "happened
 * to": `buildSaunaRoomCore` renders exactly the runs
 * {@link resolveEffectiveLayout} hands out, which is the same call
 * `deriveBenchMaterials` bills from.
 *
 * Why a separate module rather than the resolver: `interiorLayoutResolver`
 * imports `twoRoomSauna` (via `heaterClearanceLayout`), so an engine cannot
 * import it back without closing a cycle — the same constraint that made
 * `calculateMaterials` take its bench rows as a parameter. This file is a LEAF:
 * it imports the pure interior domain and nothing from the engines except
 * types. The registry resolves the layout once and hands the plan down.
 *
 * Frame of reference: the interior domain's model frame (X across, Z along,
 * origin at the building centre) IS the engines' world frame, so plan-view
 * coordinates convert by nothing more than mm → m. The one thing the layout
 * cannot know is the seat's height, which is measured off the wall top the
 * engine builds — see {@link BenchSeatHeight}.
 */
import {
	BENCH_LEDGER_HEIGHT_MM,
	BENCH_FRAME_HEIGHT_MM,
	BENCH_SLAT_THICKNESS_MM,
	type BenchRun,
	benchRunFootprint,
	type FootprintMm,
	type HeaterPlacement,
	type HeaterSizeMm,
	heaterFootprint,
	type InteriorLayout,
	type InteriorRoom,
	type InteriorWall,
	resolveEffectiveLayout,
	wallRunsAlongX,
} from "../domain/interior";
import type { BenchSupport, BenchWallSide } from "./detailHelpers";

/**
 * Seat surface of the two sauna tiers, measured DOWN from the top of the wall.
 *
 * Both rectangular engines have always placed the lavar this way (a lav's
 * headroom is set by the ceiling, not by the floor), and
 * `heaterClearanceLayout` imports the same two numbers so its obstacle boxes
 * describe the benches the model actually draws.
 */

/**
 * How high a run's seat sits.
 *
 * `below-wall-top` is a lav: the engine subtracts it from the wall top.
 * `above-floor` is furniture with its own posts (the relax bench), where the
 * leg height in the layout is what decides the seat height.
 */
export type BenchSeatHeight =
	| { kind: "below-wall-top"; metres: number }
	| { kind: "above-floor"; metres: number };

export interface BenchRenderItem {
	/** `BenchRun.id` — lets a selection in the plan view find its meshes. */
	runId: string;
	/** Appended to the engine's name prefix, e.g. `square-upper`. */
	nameSuffix: string;
	orientation: "x" | "z";
	wallSide: BenchWallSide;
	/** Span along the wall, metres. Already clipped clear of the heater. */
	widthM: number;
	depthM: number;
	centerXM: number;
	centerZM: number;
	slatCount: number;
	slatGapMm?: number;
	woodSpecies?: InteriorWoodSpecies;
	support: BenchSupport;
	legHeightM: number;
	seatHeight: BenchSeatHeight;
	/** The foot lav's slatted front, or null for a run with nothing to close. */
	skirt: BenchSkirt | null;
	backrest?: BenchBackrest | null;
}

export interface HeaterRenderPlacement {
	centerXM: number;
	centerZM: number;
	wall: InteriorWall;
	/**
	 * False when the user has moved the unit off the placement the engine
	 * generated. The renderer's strålskydd are built against a wall the engine
	 * chose; once the unit has left them, standing them at the new position
	 * would invent shields `deriveHeaterClearanceLayout` has already stopped
	 * granting exemptions for.
	 */
	atGeneratedPlacement: boolean;
}

/**
 * Which way the aggregat's door must face, given the wall it backs onto.
 *
 * The unit's door is on a Z face and the engines never rotate its FOOTPRINT
 * (see `HeaterSizeMm`), so only the short walls constrain the rotation: backed
 * onto `back` it must open toward +Z, onto `front` toward −Z. On a long wall the
 * door faces along the room's length and the wall does not decide — the square
 * engine points it away from the front wall, the two-room engine away from the
 * divider — so this returns `null` and the engine's own value stands.
 *
 * Before this existed the rotation was a per-engine constant, and an authored
 * heater on a short wall was drawn unrotated: facing into its own wall.
 */
export function heaterDoorRotationY(wall: InteriorWall): number | null {
	if (wall === "back") return 0;
	if (wall === "front") return Math.PI;
	return null;
}

export interface InteriorRenderPlan {
	benches: BenchRenderItem[];
	heater: HeaterRenderPlacement;
	/**
	 * Plan-view centre of the hot room's bench band — where an interior camera
	 * should aim. Null when the layout has no runs in the hot room.
	 */
	benchFocusM: { xM: number; zM: number } | null;
}

export interface CreateInteriorRenderPlanOptions {
	layout: InteriorLayout;
	/** Every room the layout may place runs in, hot room first. */
	rooms: readonly InteriorRoom[];
	heaterSize: HeaterSizeMm;
	/** The placement the engine itself would have picked. See `atGeneratedPlacement`. */
	generatedHeater: HeaterPlacement;
	/** Engine mesh-name prefix, `"square"` or `"two-room"`. */
	namePrefix: string;
}

const WALL_SIDE: Record<InteriorWall, BenchWallSide> = {
	back: "-z",
	front: "+z",
	left: "-x",
	right: "+x",
};

export function benchSeatHeight(run: BenchRun): BenchSeatHeight {
	if (run.seatHeightMm !== undefined)
		return {
			kind: "above-floor",
			metres: (run.seatHeightMm - BENCH_SLAT_THICKNESS_MM / 2) / 1000,
		};
	// A run that carries its own leg height is furniture standing on the floor:
	// its seat is one leg plus half a slat above it. Everything else is a lav,
	// hung at a fixed distance below the wall top.
	if (run.legHeightMm !== undefined && run.legHeightMm > 0) {
		return {
			kind: "above-floor",
			metres: (run.legHeightMm + BENCH_SLAT_THICKNESS_MM / 2) / 1000,
		};
	}
	return {
		kind: "below-wall-top",
		metres:
			run.tier === "upper"
				? UPPER_BENCH_TOP_BELOW_WALL_TOP_M
				: LOWER_BENCH_TOP_BELOW_WALL_TOP_M,
	};
}

/**
 * World Y of a run's slat centre — the mesh position `addSlattedBench` is given.
 *
 * The ONE place a {@link BenchSeatHeight} turns into a height. The engines call
 * it to place the mesh and `heaterClearanceLayout` calls it to build the
 * obstacle box, so a lav cannot be drawn at one height and measured at another.
 */
export function benchSeatCentreYM(
	seatHeight: BenchSeatHeight,
	wallBaseY: number,
	wallHeightM: number,
): number {
	return seatHeight.kind === "below-wall-top"
		? wallBaseY + wallHeightM - seatHeight.metres
		: wallBaseY + seatHeight.metres;
}

/** Vertical extent of the built bench, floor-side and seat-side, in metres. */
export interface BenchVerticalExtentM {
	minY: number;
	maxY: number;
}

/**
 * How much room a run occupies in Y, exactly as `addSlattedBench` draws it: the
 * slat top down to whatever carries it — the front legs when it has posts, the
 * wall ledger when it is hung.
 *
 * The clearance check needs this because `calculateHeaterClearance` skips any
 * obstacle that does not overlap the heater's height band. Deriving the height
 * from `tier` a second time (as this module's consumer used to) puts a run that
 * carries its own `legHeightMm` — a low authored övre lav, drawn standing on the
 * floor beside the aggregat — up near the wall top in the compliance frame,
 * where it overlaps nothing and reports no violation.
 */
export function benchVerticalExtentM(
	run: BenchRun,
	wallBaseY: number,
	wallHeightM: number,
	room?: InteriorRoom,
): BenchVerticalExtentM {
	const maxY =
		benchSeatCentreYM(benchSeatHeight(run), wallBaseY, wallHeightM) +
		BENCH_SLAT_THICKNESS_MM / 2000;
	// Posts reach the floor; a wall-hung run stops at the bärlina's underside.
	// Same test `benchItem` applies before it asks for legs at all.
	const { support, legHeightMm } = resolveBenchSupport(run, room);
	const underMm =
		legHeightMm > 0
			? legHeightMm
			: BENCH_LEDGER_HEIGHT_MM +
				(support === "endWalls" ? BENCH_FRAME_HEIGHT_MM : 0);
	const backrest = room ? resolveBenchBackrest(run, room) : null;
	return { minY: maxY - (BENCH_SLAT_THICKNESS_MM + underMm) / 1000,
		maxY: backrest ? wallBaseY + backrest.boards.at(-1)!.maxYMm / 1000 : maxY };
}

function footprintCentre(box: FootprintMm) {
	return {
		xM: (box.minXMm + box.maxXMm) / 2000,
		zM: (box.minZMm + box.maxZMm) / 2000,
	};
}

/**
 * Mesh-name suffix for a run.
 *
 * The FIRST run of a tier in a room keeps the bare name the engines have always
 * used (`square-upper`, `two-room-relax`), so nothing that reads a bench mesh by
 * name — the scale-figure placement, the collision exemptions, the audit
 * prefixes — has to learn a new vocabulary for a design nobody has edited.
 * Additional runs on the same tier (an L- or U-shape's side lavar) are numbered
 * from 2, so adding one never renames the run that was already there.
 */
function nameSuffixFor(run: BenchRun, used: Map<string, number>): string {
	const base = run.room === "relax" ? "relax" : run.tier;
	const seen = used.get(base) ?? 0;
	used.set(base, seen + 1);
	return seen === 0 ? base : `${base}-${seen + 1}`;
}

function benchItem(
	run: BenchRun,
	room: InteriorRoom,
	namePrefix: string,
	used: Map<string, number>,
): BenchRenderItem {
	const box = benchRunFootprint(run, room);
	const centre = footprintCentre(box);
	const alongX = wallRunsAlongX(run.wall);
	const { support, legHeightMm } = resolveBenchSupport(run, room);

	return {
		runId: run.id,
		nameSuffix: `${namePrefix}-${nameSuffixFor(run, used)}`,
		orientation: alongX ? "x" : "z",
		wallSide: WALL_SIDE[run.wall],
		widthM: run.lengthMm / 1000,
		depthM: run.depthMm / 1000,
		centerXM: centre.xM,
		centerZM: centre.zM,
		slatCount: resolveBenchSlats(run).count,
		...(run.slatGapMm === undefined ? {} : {slatGapMm: run.slatGapMm}),
		...(run.woodSpecies === undefined ? {} : {woodSpecies: run.woodSpecies}),
		support,
		legHeightM: legHeightMm / 1000,
		seatHeight: benchSeatHeight(run),
		skirt: resolveBenchSkirt(run, room),
		backrest: resolveBenchBackrest(run, room),
	};
}

function placementsMatch(a: HeaterPlacement, b: HeaterPlacement): boolean {
	// 0.1 mm: the layout's coordinates are rounded to a tenth (see
	// `interiorLayoutResolver`), so an untouched heater round-trips to within
	// half of that rather than exactly.
	const TOLERANCE_MM = 0.1;
	return (
		a.wall === b.wall &&
		Math.abs(a.offsetMm - b.offsetMm) <= TOLERANCE_MM &&
		Math.abs(a.wallGapMm - b.wallGapMm) <= TOLERANCE_MM
	);
}

/**
 * Resolve a layout into drawable geometry.
 *
 * Runs are clipped against the heater HERE, by the domain's
 * `resolveEffectiveLayout` — the same call the BOM makes — instead of by the
 * renderer's own `clipBenchSpanToHeater`. Two clippers with the same constants
 * still drift; one clipper cannot.
 */
export function createInteriorRenderPlan(
	options: CreateInteriorRenderPlanOptions,
): InteriorRenderPlan {
	const { layout, rooms, heaterSize, generatedHeater, namePrefix } = options;
	const hotRoom = rooms.find((room) => room.id === "sauna") ?? rooms[0];

	const benches: BenchRenderItem[] = [];
	const used = new Map<string, number>();
	let focusBox: FootprintMm | null = null;

	for (const room of rooms) {
		// The aggregat stands in the hot room; a relax run is never clipped by it.
		const { runs } = resolveEffectiveLayout(
			layout,
			room,
			room.id === "sauna" ? heaterSize : null,
		);
		for (const entry of runs) {
			benches.push(benchItem(entry.effective, room, namePrefix, used));
			if (room.id !== "sauna") continue;
			focusBox = focusBox
				? {
						minXMm: Math.min(focusBox.minXMm, entry.footprint.minXMm),
						maxXMm: Math.max(focusBox.maxXMm, entry.footprint.maxXMm),
						minZMm: Math.min(focusBox.minZMm, entry.footprint.minZMm),
						maxZMm: Math.max(focusBox.maxZMm, entry.footprint.maxZMm),
					}
				: entry.footprint;
		}
	}

	const heaterBox = heaterFootprint(layout.heater, hotRoom, heaterSize);
	const heaterCentre = footprintCentre(heaterBox);

	return {
		benches,
		heater: {
			centerXM: heaterCentre.xM,
			centerZM: heaterCentre.zM,
			wall: layout.heater.wall,
			atGeneratedPlacement: placementsMatch(layout.heater, generatedHeater),
		},
		benchFocusM: focusBox ? footprintCentre(focusBox) : null,
	};
}
